/**
 * Display-only NMI card metadata from Collect.js tokenization callbacks.
 * Mirrors `supabase/functions/_shared/nmi-card-display.ts` for the browser.
 */

/**
 * @param {unknown} tokenResp Collect.js callback response (`{ token, card, ... }`)
 * @returns {{ brand: string, last4: string, exp_month?: number, exp_year?: number } | null}
 */
export function extractNmiCardDisplayFromCollectJs(tokenResp) {
  const card = tokenResp?.card;
  if (!card || typeof card !== 'object') return null;

  const digits = String(card.number || '').replace(/\D/g, '');
  const last4 = digits.length >= 4 ? digits.slice(-4) : null;
  if (!last4) return null;

  let brand = String(card.type || '').trim().toLowerCase();
  if (brand === 'american express') brand = 'amex';
  if (!brand) brand = 'card';

  const expRaw = String(card.exp || '').replace(/\D/g, '');
  if (expRaw.length !== 4) {
    return { brand, last4 };
  }

  const exp_month = parseInt(expRaw.slice(0, 2), 10);
  const exp_year = 2000 + parseInt(expRaw.slice(2, 4), 10);
  if (!Number.isInteger(exp_month) || exp_month < 1 || exp_month > 12) {
    return { brand, last4 };
  }
  if (!Number.isInteger(exp_year) || exp_year < 2000) {
    return { brand, last4 };
  }

  return { brand, last4, exp_month, exp_year };
}

/**
 * Read stored card-on-file display fields from booking flight_data.
 * @param {Record<string, unknown> | null | undefined} flightData
 * @returns {{ brand: string, last4: string, expMonth?: number, expYear?: number } | null}
 */
export function getNmiCardOnFile(flightData) {
  if (!flightData || typeof flightData !== 'object') return null;
  const last4 = flightData._nmi_card_last4;
  const brand = flightData._nmi_card_brand;
  if (typeof last4 !== 'string' || last4.length !== 4) return null;
  if (typeof brand !== 'string' || !brand.trim()) return null;

  const expMonth = Number(flightData._nmi_card_exp_month);
  const expYear = Number(flightData._nmi_card_exp_year);
  const hasExp = Number.isInteger(expMonth) && expMonth >= 1 && expMonth <= 12
    && Number.isInteger(expYear) && expYear >= 2000;

  return {
    brand: brand.trim().toLowerCase(),
    last4,
    ...(hasExp ? { expMonth, expYear } : {}),
  };
}

/** @param {string} brand */
export function formatNmiCardBrandLabel(brand) {
  if (!brand) return 'Card';
  const b = String(brand).toLowerCase();
  if (b === 'amex') return 'Amex';
  if (b === 'mastercard') return 'Mastercard';
  if (b === 'visa') return 'Visa';
  if (b === 'discover') return 'Discover';
  if (b === 'diners') return 'Diners';
  if (b === 'jcb') return 'JCB';
  return b.charAt(0).toUpperCase() + b.slice(1);
}
